import { useEffect } from "react";

/**
 * Imperatively set per-route SEO without pulling in react-helmet.
 *
 * Side-effects per render:
 *   - <title>                       (brand suffix appended unless homepage)
 *   - <meta name="description">
 *   - <meta property="og:title">
 *   - <meta property="og:description">
 *   - <meta property="og:url">      (absolute URL of current route)
 *   - <meta property="og:type">     ("website" by default, "article" for blog posts)
 *   - <meta property="og:image">    (optional)
 *   - <link rel="canonical">        (CRITICAL — previously all pages claimed
 *                                     to be the homepage, which broke Semrush
 *                                     and Google's sitemap validation)
 *
 * On unmount the previous values are restored so leaving a page doesn't leak
 * its SEO into the next one (matters because the SPA never reloads).
 */
const BRAND = "Endurance Casualty Insurance Agency";
const DEFAULT_TITLE = BRAND;
const DEFAULT_DESCRIPTION =
    "Endurance Casualty Insurance Agency — licensed independent insurance agency. Request a quote and a licensed agent will follow up by phone or email.";

// Canonical production host. The site is also reachable on the www subdomain,
// but we collapse everything onto the apex to avoid duplicate-content signals.
const CANONICAL_ORIGIN = "https://endurancecasualty.com";

function ensureMeta(selector, createAttrs) {
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("meta");
        Object.entries(createAttrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
    }
    return el;
}

function ensureLink(rel) {
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    return el;
}

function buildTitle(rawTitle) {
    if (!rawTitle) return DEFAULT_TITLE;
    // Avoid double-branding (e.g. seo_title already ends with the brand).
    if (rawTitle.includes(BRAND)) return rawTitle;
    return `${rawTitle} | ${BRAND}`;
}

function buildCanonical(path) {
    // Always canonicalise to apex host. Normalise trailing slashes so
    // ``/blog/foo/`` and ``/blog/foo`` resolve to the same canonical.
    let p = path || "/";
    if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
    return `${CANONICAL_ORIGIN}${p}`;
}

export default function useSeo({
    title,
    description,
    canonicalPath,
    ogType = "website",
    ogImage,
} = {}) {
    useEffect(() => {
        const resolvedTitle = buildTitle(title);
        const resolvedDescription = description || DEFAULT_DESCRIPTION;
        const path =
            canonicalPath ||
            (typeof window !== "undefined" ? window.location.pathname : "/");
        const canonicalUrl = buildCanonical(path);

        // --- <title> -------------------------------------------------------
        const previousTitle = document.title;
        document.title = resolvedTitle;

        // --- <meta name="description"> -------------------------------------
        const descEl = ensureMeta('meta[name="description"]', {
            name: "description",
        });
        const previousDesc = descEl.getAttribute("content");
        descEl.setAttribute("content", resolvedDescription);

        // --- og:title ------------------------------------------------------
        const ogTitleEl = ensureMeta('meta[property="og:title"]', {
            property: "og:title",
        });
        const previousOgTitle = ogTitleEl.getAttribute("content");
        ogTitleEl.setAttribute("content", resolvedTitle);

        // --- og:description -----------------------------------------------
        const ogDescEl = ensureMeta('meta[property="og:description"]', {
            property: "og:description",
        });
        const previousOgDesc = ogDescEl.getAttribute("content");
        ogDescEl.setAttribute("content", resolvedDescription);

        // --- og:url --------------------------------------------------------
        const ogUrlEl = ensureMeta('meta[property="og:url"]', {
            property: "og:url",
        });
        const previousOgUrl = ogUrlEl.getAttribute("content");
        ogUrlEl.setAttribute("content", canonicalUrl);

        // --- og:type -------------------------------------------------------
        const ogTypeEl = ensureMeta('meta[property="og:type"]', {
            property: "og:type",
        });
        const previousOgType = ogTypeEl.getAttribute("content");
        ogTypeEl.setAttribute("content", ogType);

        // --- og:image (optional, only when caller supplies one) -----------
        let ogImageEl = null;
        let previousOgImage = null;
        if (ogImage) {
            ogImageEl = ensureMeta('meta[property="og:image"]', {
                property: "og:image",
            });
            previousOgImage = ogImageEl.getAttribute("content");
            ogImageEl.setAttribute("content", ogImage);
        }

        // --- <link rel="canonical"> ---------------------------------------
        const canonicalEl = ensureLink("canonical");
        const previousCanonical = canonicalEl.getAttribute("href");
        canonicalEl.setAttribute("href", canonicalUrl);

        return () => {
            // Restore so leaving a page doesn't leak its SEO into the next one.
            document.title = previousTitle;
            if (previousDesc != null) descEl.setAttribute("content", previousDesc);
            if (previousOgTitle != null)
                ogTitleEl.setAttribute("content", previousOgTitle);
            if (previousOgDesc != null)
                ogDescEl.setAttribute("content", previousOgDesc);
            if (previousOgUrl != null)
                ogUrlEl.setAttribute("content", previousOgUrl);
            if (previousOgType != null)
                ogTypeEl.setAttribute("content", previousOgType);
            if (ogImageEl && previousOgImage != null)
                ogImageEl.setAttribute("content", previousOgImage);
            if (previousCanonical != null)
                canonicalEl.setAttribute("href", previousCanonical);
        };
    }, [title, description, canonicalPath, ogType, ogImage]);
}
